import React, { Component } from 'react';

import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { LinkImage } from '@components';
import { 
  CampingSite, 
  getCampingSites, 
  getStates, 
  getCities, 
  getCountries,
  filterByState, 
  filterByCity, 
  filterByCountry,
  filterByName 
} from '../../../services/campingData';
import heroImage from '../../../assets/images/campings/hero-image.jpg';

import './style.scss';

interface IProps extends WrappedComponentProps, RouteComponentProps {}

interface IState {
  selectedState: string;
  selectedCity: string;
  selectedCountry: string;
  searchTerm: string;
  campingSites: CampingSite[];
  allSites: CampingSite[];
  states: string[];
  cities: string[];
  countries: string[];
  loading: boolean;
  copiedCode: string | null;
  expandedAmenities: string | null;
}

class CampingsComponent extends Component<IProps, IState> {

  state: IState = { 
    selectedState: 'All States', 
    selectedCity: 'All Cities', 
    selectedCountry: 'All Countries',
    searchTerm: '',
    campingSites: [],
    allSites: [],
    states: [],
    cities: [],
    countries: [],
    loading: true,
    copiedCode: null,
    expandedAmenities: null
  };

  async componentDidMount() {
    await this.loadCampingData();
  }

  loadCampingData = async () => {
    try {
      this.setState({ loading: true });
      const sites = await getCampingSites();
      const states = getStates(sites);
      const cities = getCities(sites);
      const countries = getCountries(sites);
      
      this.setState({ 
        campingSites: sites, 
        allSites: sites,
        states: ['All States', ...states],
        cities: ['All Cities', ...cities],
        countries: ['All Countries', ...countries],
        loading: false 
      });
    } catch (error) {
      console.error('Error loading camping data:', error);
      this.setState({ loading: false });
    }
  };


  getFilteredSites = () => {
    const { selectedState, selectedCity, selectedCountry, searchTerm, allSites } = this.state;
    
    let filtered = allSites;
    
    // Filter by country
    if (selectedCountry && selectedCountry !== 'All Countries') {
      filtered = filterByCountry(filtered, selectedCountry);
    }
    
    // Filter by state
    if (selectedState && selectedState !== 'All States') {
      filtered = filterByState(filtered, selectedState);
    }
    
    // Filter by city
    if (selectedCity && selectedCity !== 'All Cities') {
      filtered = filterByCity(filtered, selectedCity);
    }
    
    // Search by name
    if (searchTerm) {
      filtered = filterByName(filtered, searchTerm);
    }
    
    return filtered;
  };

  handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = event.target.value;
    this.setState({ 
      selectedCountry,
      selectedState: 'All States', // Reset state when country changes
      selectedCity: 'All Cities' // Reset city when country changes
    });
  };

  handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedState = event.target.value;
    this.setState({ 
      selectedState,
      selectedCity: 'All Cities' // Reset city when state changes
    });
  };

  handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ selectedCity: event.target.value });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchTerm: event.target.value });
  };

  resetFilters = () => {
    this.setState({
      selectedCountry: 'All Countries',
      selectedState: 'All States',
      selectedCity: 'All Cities',
      searchTerm: ''
    });
  };

         copyCouponCode = async (couponCode: string, siteId: string) => {
           try {
             await navigator.clipboard.writeText(couponCode);
             this.setState({ copiedCode: siteId });

             // Clear the copied state after 2 seconds
             setTimeout(() => {
               this.setState({ copiedCode: null });
             }, 2000);
           } catch (err) {
             console.error('Failed to copy coupon code:', err);
             // Fallback for older browsers
             const textArea = document.createElement('textarea');
             textArea.value = couponCode;
             document.body.appendChild(textArea);
             textArea.select();
             document.execCommand('copy');
             document.body.removeChild(textArea);

             this.setState({ copiedCode: siteId });
             setTimeout(() => {
               this.setState({ copiedCode: null });
             }, 2000);
           }
         };

         toggleAmenities = (siteTitle: string) => {
           this.setState(prevState => ({
             expandedAmenities: prevState.expandedAmenities === siteTitle ? null : siteTitle
           }));
         };

  render(): React.ReactNode {
    const { intl } = this.props;
    const { selectedState, selectedCity, selectedCountry, searchTerm, loading, copiedCode, states, cities, countries, expandedAmenities } = this.state;
    const filteredSites = this.getFilteredSites();

    return (
      <div className="campings">
        {/* Hero Section with Single Image */}
        <div className="campings__hero">
          <div className="campings__hero-content">
            <h1 className="campings__hero-title">
              <FormattedMessage id="campings.hero.title" defaultMessage="The Ultimate Camping Network" />
            </h1>
            <p className="campings__hero-subtitle">
              <FormattedMessage id="campings.hero.subtitle" defaultMessage="Access to over 140 resorts across the nation with friendly, first-class service" />
            </p>
            <p className="campings__hero-price">
              <FormattedMessage id="campings.hero.price" defaultMessage="Camp For Just $9.00 a Night!" />
            </p>
          </div>
        </div>

        {/* Filter Section - Replaced Search with Filters */}
        <div className="campings__filter-section">
          <div className="container">
            <div className="campings__filter-controls">
              <div className="campings__filter-group">
                <label>Filter by Country:</label>
                <select value={selectedCountry} onChange={this.handleCountryChange}>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              <div className="campings__filter-group">
                <label>Filter by State:</label>
                <select value={selectedState} onChange={this.handleStateChange}>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="campings__filter-group">
                <label>Filter by City:</label>
                <select value={selectedCity} onChange={this.handleCityChange}>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="campings__filter-group">
                <label>Search by Name:</label>
                <input
                  type="text"
                  placeholder="Search by resort name..."
                  value={searchTerm}
                  onChange={this.handleSearchChange}
                />
              </div>
            </div>
            <div className="campings__results-count">
              <div className="campings__results-text">
                Showing {filteredSites.length} of {this.state.allSites.length} camping sites
              </div>
              {(selectedCountry !== 'All Countries' || selectedState !== 'All States' || selectedCity !== 'All Cities' || searchTerm) && (
                <button 
                  className="campings__reset-filters"
                  onClick={this.resetFilters}
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Camping Sites Grid */}
        <div className="campings__sites-section">
          <div className="container">
            <div className="campings__sites-header">
              <h2><FormattedMessage id="campings.sites.title" defaultMessage="140+ Resorts Across The USA, Canada, and Mexico" /></h2>
              <p><FormattedMessage id="campings.sites.subtitle" defaultMessage="Experience our affiliated camping system and enjoy resorts with an array of activities and amenities" /></p>
            </div>

            {loading ? (
              <div className="campings__loading">
                <p><FormattedMessage id="campings.loading" defaultMessage="Loading camping sites..." /></p>
              </div>
            ) : (
              <div className="campings__sites-grid">
                {filteredSites.map(site => (
                <div key={site.id} className="campings__site-card">
                  <div className="campings__site-image">
                    {site.photo && site.photo.trim() !== '' && site.photo.startsWith('http') ? (
                      <img src={site.photo} alt={site.title} />
                    ) : (
                      <div className="campings__site-placeholder">
                        <img src="/src/assets/images/campings/tiles/Hero-Placeholder.jpg" alt={site.title} />
                      </div>
                    )}
                    {site.featured && (
                      <div className="campings__site-featured">Featured</div>
                    )}
                  </div>
                  <div className="campings__site-content">
                    <h3 title={site.title}>{site.title}</h3>
                    <p className="campings__site-location">
                      {site.city}, {site.state} {site.country}
                    </p>
                    <p 
                      className="campings__site-description"
                      title={site.longDescription ? site.longDescription.replace(/<[^>]*>/g, '') : 'Beautiful camping destination with great amenities.'}
                    >
                      {(() => {
                        const cleanDescription = site.longDescription ? 
                          site.longDescription.replace(/<[^>]*>/g, '') : 
                          'Beautiful camping destination with great amenities.';
                        
                        const words = cleanDescription.split(' ');
                        if (words.length > 28) {
                          return words.slice(0, 28).join(' ') + '...';
                        }
                        return cleanDescription;
                      })()}
                    </p>
                    
                    {/* Site Details */}
                    <div className="campings__site-details">
                      {site.phone && (
                        <div className="campings__site-phone">
                          📞 {site.phone.trim()}
                        </div>
                      )}
                      {site.fullSites && (
                        <div className="campings__site-sites">
                          🚐 {site.fullSites} Full Sites
                        </div>
                      )}
                      {site.maxElectrical && (
                        <div className="campings__site-electrical">
                          ⚡ {site.maxElectrical}
                        </div>
                      )}
                    </div>

                    {/* Amenities */}
                    <div className="campings__amenities-container">
                      <div
                        className="campings__site-amenities"
                        data-tooltip="Pool, Hot Tub, Wifi, Pets, Pet Farm"
                      >
                        {["Pool", "Hot Tub", "Wifi", "Pets", "Pet Farm"].map((amenity, index) => (
                          <span key={index} className="campings__amenity-tag">
                            {amenity}
                          </span>
                        ))}
                        <button
                          className="campings__amenities-expand"
                          onClick={() => this.toggleAmenities(site.title)}
                          title="Show all amenities"
                        >
                          +
                        </button>
                      </div>
                      {expandedAmenities === site.title && (
                        <div className="campings__amenities-expanded">
                          <div className="campings__amenities-expanded-content">
                            <h4>All Amenities:</h4>
                            <div className="campings__amenities-expanded-list">
                              {["Pool", "Hot Tub", "Wifi", "Pets", "Pet Farm"].map((amenity, index) => (
                                <span key={index} className="campings__amenity-tag">
                                  {amenity}
                                </span>
                              ))}
                            </div>
                            <button
                              className="campings__amenities-close"
                              onClick={() => this.toggleAmenities(site.title)}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Coupon Code Section */}
                    <div className="campings__site-coupon">
                      <div className="campings__coupon-story">
                        <div className="campings__coupon-title">
                          <FormattedMessage id="campings.coupon.exclusive" defaultMessage="Exclusive Member Deal" />
                        </div>
                        <div className="campings__coupon-description">
                          <FormattedMessage 
                            id="campings.coupon.story" 
                            defaultMessage="As a valued member, you get exclusive access to our premium camping experience at just $9/night - that's over 70% off regular rates!" 
                          />
                        </div>
                      </div>
                             <div className="campings__coupon-code-section">
                               <div className="campings__coupon-label">
                                 <FormattedMessage id="campings.coupon.use_code" defaultMessage="Use Code" />
                               </div>
                               <div
                                 className={`campings__coupon-code ${copiedCode === site.title ? 'copied' : ''}`}
                                 onClick={() => this.copyCouponCode('UCN2024', site.title)}
                                 title="Click to copy"
                               >
                                 {copiedCode === site.title ? (
                                   <span className="campings__copied-text">
                                     <FormattedMessage id="campings.coupon.copied" defaultMessage="✓ Copied!" />
                                   </span>
                                 ) : (
                                   'UCN2024'
                                 )}
                               </div>
                             </div>
                    </div>

                    {/* Website Link */}
                    {site.website && (
                      <div className="campings__site-website">
                        <a href={site.website} target="_blank" rel="noopener noreferrer">
                          Visit Website →
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                ))}
              </div>
            )}

            {!loading && filteredSites.length === 0 && (
              <div className="campings__no-results">
                <p><FormattedMessage id="campings.no_results" defaultMessage="No camping sites found matching your criteria." /></p>
              </div>
            )}
          </div>
        </div>

      </div>
    );
  }
}

export const Campings = connect()(injectIntl(withRouter(CampingsComponent)));
